/**
 * EditModal Component
 * Modal for editing a comment's text with keyboard shortcuts.
 */
import React, { useState, useRef, useEffect } from 'react';
import type { Comment } from '../../types';

interface EditModalProps {
  comment: Comment;
  isOpen: boolean;
  onSave: (commentId: string, newText: string) => Promise<void>;
  onClose: () => void;
}

const EditModal: React.FC<EditModalProps> = ({
  comment,
  isOpen,
  onSave,
  onClose,
}) => {
  const [editedText, setEditedText] = useState(comment.suggestion);
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isOpen]);

  // Reset text when comment changes
  useEffect(() => {
    setEditedText(comment.suggestion);
  }, [comment.suggestion]);

  const handleSave = async () => {
    if (editedText.trim() === comment.suggestion) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(comment.id, editedText.trim());
      onClose();
    } catch {
      // Error is handled by the hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Calculate character difference
  const charDiff = editedText.length - comment.suggestion.length;
  const charDiffText =
    charDiff === 0
      ? 'No change'
      : charDiff > 0
        ? `+${charDiff} characters`
        : `${charDiff} characters`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 animate-backdrop-enter"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden animate-modal-enter">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Comment</h3>
          <p className="text-sm text-gray-500 mt-1">
            Editing feedback for: &quot;{comment.highlightedText.slice(0, 50)}
            {comment.highlightedText.length > 50 ? '...' : ''}&quot;
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Suggestion
          </label>
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="Enter your feedback..."
            disabled={isSaving}
          />

          <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
            <span>{editedText.length} characters</span>
            <span
              className={
                charDiff === 0
                  ? 'text-gray-500'
                  : charDiff > 0
                    ? 'text-green-600'
                    : 'text-red-600'
              }
            >
              {charDiffText}
            </span>
          </div>

          <p className="mt-4 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-md">
            Your edit helps the AI learn your feedback style.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl+Enter</kbd>{' '}
            to save,{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd> to
            cancel
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || editedText.trim() === ''}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
